// Camada de dados das telas de seller do admin (`spec/17-frontend.md` §8.2, WEB-21).
//
// Nenhum tipo de resposta escrito à mão: tudo derivado de `api/types/admin.d.ts`
// (`spec/17` §5.2).
//
// CONTRATO DE QUERY KEY (`app/live-events.ts`): domínio primeiro. Aqui o domínio é
// `admin-sellers`, e ele NÃO está no mapa de invalidação de `spec/17` §4.5, que só
// cobre as keys do seller. Não é esquecimento: o shell do admin (`routes/admin.tsx`)
// não chama `useLiveEvents()`, o feed `GET /events` é o do seller logado. Quem mantém a
// tela fresca é o `refetchOnWindowFocus` global mais o `refetchInterval` da lista, e as
// mutações invalidam o prefixo na mão. Registrado em "Questões abertas" da task.

import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { api } from '../client.ts';
import type { paths } from '../types/admin.d.ts';

type ListOk = paths['/admin/v1/sellers']['get']['responses'][200]['content']['application/json'];
type DetailOk =
  paths['/admin/v1/sellers/{id}']['get']['responses'][200]['content']['application/json'];
type LedgerOk =
  paths['/admin/v1/sellers/{id}/ledger']['get']['responses'][200]['content']['application/json'];
type ReportOk =
  paths['/admin/v1/reports/sellers/{id}']['get']['responses'][200]['content']['application/json'];
type AcquirerOk =
  paths['/admin/v1/sellers/{id}/acquirer']['get']['responses'][200]['content']['application/json'];
type RatesOk =
  paths['/admin/v1/sellers/{id}/rates']['get']['responses'][200]['content']['application/json'];
type AcquirersOk =
  paths['/admin/v1/acquirers']['get']['responses'][200]['content']['application/json'];

export type AdminSellerListPage = ListOk;
export type AdminSellerRow = ListOk['data'][number];
export type AdminSellerDetail = DetailOk;
export type AdminSellerStatus = AdminSellerRow['status'];
export type AdminKycStatus = AdminSellerRow['kyc_status'];
export type AdminSellerLedgerPage = LedgerOk;
export type AdminSellerLedgerEntry = LedgerOk['data'][number];
export type AdminSellerReport = ReportOk;
export type AdminSellerAcquirer = AcquirerOk;
export type AdminSellerRates = RatesOk;
export type RateVersion = RatesOk['current'];
export type AdminAcquirer = AcquirersOk['data'][number];

export type ReasonBody =
  paths['/admin/v1/sellers/{id}/suspend']['post']['requestBody']['content']['application/json'];
export type FlagsBody =
  paths['/admin/v1/sellers/{id}/flags']['patch']['requestBody']['content']['application/json'];
export type AcquirerBody =
  paths['/admin/v1/sellers/{id}/acquirer']['put']['requestBody']['content']['application/json'];
export type RatesBody =
  paths['/admin/v1/sellers/{id}/rates']['put']['requestBody']['content']['application/json'];
export type SplitQuotaBody =
  paths['/admin/v1/sellers/{id}/split-quota']['patch']['requestBody']['content']['application/json'];
export type SplitQuota =
  paths['/admin/v1/sellers/{id}/split-quota']['patch']['responses'][200]['content']['application/json'];

/** `reason` de suspender/reativar/encerrar: `z.string().min(10)` na API. */
export const REASON_MIN_LENGTH = 10;

/** O admin não tem toggle global de modo: escolhe por tela (`spec/17` §4.4). */
export type AdminMode = 'live' | 'test';

export interface AdminSellerFilters {
  status?: AdminSellerStatus | undefined;
  kyc_status?: AdminKycStatus | undefined;
  q?: string | undefined;
  /** O item "Sinalizados" da sidebar: `?flags=1` do croqui não existe (`spec/17` §8.3). */
  payout_locked?: boolean | undefined;
  /**
   * Fila de encerramento (IOS-03, `spec/26` §4.4). Ligado, a API também troca a
   * ordenação da página: pedido mais antigo primeiro.
   */
  closure_requested?: boolean | undefined;
}

/** `limit` default de `spec/17` §4.7. */
export const PAGE_LIMIT = 50;

export const adminSellersKeys = {
  all: ['admin-sellers'] as const,
  list: (filters: AdminSellerFilters) => ['admin-sellers', 'list', filters] as const,
  detail: (id: string) => ['admin-sellers', id] as const,
  lookup: (q: string) => ['admin-sellers', 'lookup', q] as const,
  ledger: (id: string, mode: AdminMode) => ['admin-sellers', id, 'ledger', mode] as const,
  report: (id: string, mode: AdminMode) => ['admin-sellers', id, 'report', mode] as const,
  acquirer: (id: string) => ['admin-sellers', id, 'acquirer'] as const,
  rates: (id: string) => ['admin-sellers', id, 'rates'] as const,
  acquirers: ['admin-sellers', 'acquirers'] as const,
};

/**
 * `payout_locked` vai como `'true'`/`'false'`: a API valida com `z.enum(['true','false'])`
 * porque `Boolean('false')` é `true` (comentário do próprio `adminListQuerySchema`).
 * Filtro ausente some da query, mandar `payout_locked=false` esconderia o seller travado.
 */
export function queryForFilters(
  filters: AdminSellerFilters,
): Record<string, string | number | undefined> {
  const query: Record<string, string | number | undefined> = { limit: PAGE_LIMIT };
  if (filters.status !== undefined) {
    query.status = filters.status;
  }
  if (filters.kyc_status !== undefined) {
    query.kyc_status = filters.kyc_status;
  }
  if (filters.q !== undefined && filters.q !== '') {
    query.q = filters.q;
  }
  if (filters.payout_locked === true) {
    query.payout_locked = 'true';
  }
  if (filters.closure_requested === true) {
    query.closure_requested = 'true';
  }
  return query;
}

/** Algum filtro fora do default: muda "vazio" para "sem resultado". */
export function hasFilters(filters: AdminSellerFilters): boolean {
  return (
    filters.status !== undefined ||
    filters.kyc_status !== undefined ||
    (filters.q !== undefined && filters.q !== '') ||
    filters.payout_locked === true ||
    filters.closure_requested === true
  );
}

/** Polling de 30 s: o fallback obrigatório de `spec/17` §4.5 para a lista aberta. */
export function adminSellersListOptions(filters: AdminSellerFilters) {
  return infiniteQueryOptions({
    queryKey: adminSellersKeys.list(filters),
    queryFn: ({ pageParam, signal }) =>
      api<AdminSellerListPage>('/admin/v1/sellers', {
        query: { ...queryForFilters(filters), cursor: pageParam },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: AdminSellerListPage) => page.next_cursor ?? undefined,
    refetchInterval: 30_000,
  });
}

/** Quantos sellers o seletor de filtro mostra por busca. */
export const LOOKUP_LIMIT = 8;

/**
 * Busca de seller para o FILTRO das listas (transações, saques) e para o chip que mostra o
 * seller filtrado. Usa a lista (`?q=`, casa id, e-mail, documento e nomes), nunca o
 * detalhe: abrir o detalhe grava `seller.view` na auditoria, filtrar não é abrir.
 */
export function adminSellerLookupOptions(q: string, enabled: boolean) {
  return queryOptions({
    queryKey: adminSellersKeys.lookup(q),
    queryFn: ({ signal }) =>
      api<AdminSellerListPage>('/admin/v1/sellers', { query: { q, limit: LOOKUP_LIMIT }, signal }),
    enabled,
    staleTime: 30_000,
  });
}

export function adminSellerOptions(id: string) {
  return queryOptions({
    queryKey: adminSellersKeys.detail(id),
    queryFn: ({ signal }) => api<AdminSellerDetail>(`/admin/v1/sellers/${id}`, { signal }),
    retry: false,
    // Cada leitura grava `seller.view`: voltar pra aba não é abrir o seller de novo.
    refetchOnWindowFocus: false,
  });
}

export function adminSellerLedgerOptions(id: string, mode: AdminMode, enabled: boolean) {
  return infiniteQueryOptions({
    queryKey: adminSellersKeys.ledger(id, mode),
    queryFn: ({ pageParam, signal }) =>
      api<AdminSellerLedgerPage>(`/admin/v1/sellers/${id}/ledger`, {
        query: { mode, limit: PAGE_LIMIT, cursor: pageParam },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: AdminSellerLedgerPage) => page.next_cursor ?? undefined,
    enabled,
  });
}

export function adminSellerReportOptions(id: string, mode: AdminMode, enabled: boolean) {
  return queryOptions({
    queryKey: adminSellersKeys.report(id, mode),
    queryFn: ({ signal }) =>
      api<AdminSellerReport>(`/admin/v1/reports/sellers/${id}`, {
        query: { period: '30d', mode },
        signal,
      }),
    enabled,
  });
}

export function adminSellerRatesOptions(id: string, enabled: boolean) {
  return queryOptions({
    queryKey: adminSellersKeys.rates(id),
    queryFn: ({ signal }) => api<AdminSellerRates>(`/admin/v1/sellers/${id}/rates`, { signal }),
    enabled,
  });
}

export function adminSellerAcquirerOptions(id: string, enabled: boolean) {
  return queryOptions({
    queryKey: adminSellersKeys.acquirer(id),
    queryFn: ({ signal }) =>
      api<AdminSellerAcquirer>(`/admin/v1/sellers/${id}/acquirer`, { signal }),
    enabled,
  });
}

/**
 * `GET /admin/v1/acquirers` não está na tabela de endpoints da task: entra porque fixar
 * o adquirente do seller pede um `acquirer_id`, e pedir um id cru num `Input` seria
 * empurrar o erro para o operador. Só carrega quando o formulário abre. Registrado em
 * "Questões abertas".
 */
export function adminAcquirersOptions(enabled: boolean) {
  return queryOptions({
    queryKey: adminSellersKeys.acquirers,
    queryFn: ({ signal }) => api<AcquirersOk>('/admin/v1/acquirers', { signal }),
    enabled,
  });
}

/**
 * As quatro escritas de status/flags (step-up obrigatório em todas: `ADMIN_STEP_UP_ROUTES`
 * de `apps/api/src/admin-app.ts`). Quem trata o `403 mfa_required` é `api/client.ts` -
 * nenhuma delas reimplementa o `StepUpDialog`.
 */
export function suspendSeller(id: string, body: ReasonBody): Promise<AdminSellerDetail> {
  return api<AdminSellerDetail>(`/admin/v1/sellers/${id}/suspend`, { method: 'POST', body });
}

export function reactivateSeller(id: string, body: ReasonBody): Promise<AdminSellerDetail> {
  return api<AdminSellerDetail>(`/admin/v1/sellers/${id}/reactivate`, { method: 'POST', body });
}

export function closeSeller(id: string, body: ReasonBody): Promise<AdminSellerDetail> {
  return api<AdminSellerDetail>(`/admin/v1/sellers/${id}/close`, { method: 'POST', body });
}

export function patchSellerFlags(id: string, body: FlagsBody): Promise<AdminSellerDetail> {
  return api<AdminSellerDetail>(`/admin/v1/sellers/${id}/flags`, { method: 'PATCH', body });
}

export function putSellerAcquirer(id: string, body: AcquirerBody): Promise<AdminSellerAcquirer> {
  return api<AdminSellerAcquirer>(`/admin/v1/sellers/${id}/acquirer`, { method: 'PUT', body });
}

/** Append-only: grava uma versão nova (`spec/08` §4.3), nunca edita a vigente. */
export function putSellerRates(id: string, body: RatesBody): Promise<RateVersion> {
  return api<RateVersion>(`/admin/v1/sellers/${id}/rates`, { method: 'PUT', body });
}

/** Remover o override devolve o seller ao default da plataforma. */
export function deleteSellerRates(id: string): Promise<RateVersion> {
  return api<RateVersion>(`/admin/v1/sellers/${id}/rates`, { method: 'DELETE' });
}

/**
 * Cota de split de 30 dias do seller (`spec/19` §5.4). `quota_cents: null` devolve o seller
 * ao padrão global. Step-up obrigatório: quem trata o `403 mfa_required` é `api/client.ts`.
 */
export function patchSellerSplitQuota(id: string, body: SplitQuotaBody): Promise<SplitQuota> {
  return api<SplitQuota>(`/admin/v1/sellers/${id}/split-quota`, { method: 'PATCH', body });
}
